import { Box } from "@rebass/grid";
import * as contactActions from "@rentiohq/shared-frontend/dist/redux/contact/contact.actions";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import {
  EPropertyMemberTypes,
  IProperty,
  TPropertyMember,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  Checkbox,
  DisplayText,
  ELoadingSize,
  RichTextInput,
  ESpacings,
  Loading,
  Modal,
  Switch,
  TextField,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import * as systemSelectors from "@rentiohq/web-shared/dist/redux/system/system.selectors";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import * as CONFIG from "scenes/Settings/scenes/TenantProfileNotificationPreferences/TenantProfileNotificationPreferences.config";
import { getInternalNote } from "utils/application.utils";
import { Rating } from "../../../../../../components/Rating";
import { PropertyOwnersCard } from "../../../Detail/components/PropertyOwnersCard";
import { getServerMessage } from "../VisitBooking/VisitBooking.utils";
import {
  getVisibleTextLength,
  isIntroWithinLimit,
  resolveOwnerLocale,
} from "./ownerIntro.utils";
import {
  buildOwnerPreview,
  getDefaultIntro,
  OWNER_PREVIEW_KEY,
} from "./ownerPreview.utils";
import {
  getOwnerRecipients,
  IOwnerRecipient,
  sendApplicationsToOwner,
} from "./sendToOwner.api";
import {
  buildSendToOwnerPayload,
  ESendToOwnerLevel,
  getErrorMessage,
  getPrefilledComments,
  getSuccessMessage,
  includesAssessment,
  MAX_TEXT_LENGTH,
} from "./sendToOwner.utils";
import {
  createShareNote,
  readLatestNote,
  shouldAppendComment,
} from "./sendToOwnerNote.utils";

interface IProps {
  property: IProperty;
  applications: IApplication[];
  onClose: () => void;
  onSent: () => void;
}

/**
 * A row whose control sits on the first line.
 *
 * Centring the whole row puts the box halfway down a block whose second
 * line is an explanation, which reads as floating between the two (Jonas,
 * 2026-10-06). The cell gives the control one line's height and centres it
 * in that, so it lands on the title whether or not an explanation follows.
 */
const CHECKBOX_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
};

// The dash in front of every data line of a candidate, as in the mail.
const DATA_LINE_DASH = "–";

const INLINE_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

// What the owner will see as a link. Not a link here: there is no file
// address until the mail is sent.
const LINK_PREVIEW: React.CSSProperties = {
  textDecoration: "underline",
};

const CHECKBOX_CELL: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  minHeight: "20px",
};

export const SendToOwnerModal = ({
  property,
  applications,
  onClose,
  onSent,
}: IProps) => {
  const [recipients, setRecipients] = React.useState<IOwnerRecipient[]>([]);
  const [isLoadingRecipients, setIsLoadingRecipients] = React.useState(true);
  const [selectedAccountIds, setSelectedAccountIds] = React.useState<number[]>(
    [],
  );
  // The level is the answer to one checkbox rather than a control of its
  // own: basic information always goes, and the only question is whether
  // the broker's own judgement goes with it (Jonas, 2026-10-06).
  const [includeAssessment, setIncludeAssessment] = React.useState(false);
  const [ccSender, setCcSender] = React.useState(true);
  const level = includeAssessment
    ? ESendToOwnerLevel.Assessment
    : ESendToOwnerLevel.Basic;
  // On by default at both levels: the owner is being asked to judge
  // candidates, and the file is what they judge from (Jonas, 2026-10-06).
  const [includeProfileLink, setIncludeProfileLink] = React.useState(true);
  const dispatch = useDispatch();
  const [introText, setIntroText] = React.useState("");
  // Whether the broker has touched the box. Until they have, it follows the
  // owner they picked; after, it is theirs and a later pick must not wipe
  // what they wrote.
  const editedIntro = React.useRef(false);

  const introLength = getVisibleTextLength(introText);
  const isIntroTooLong = !isIntroWithinLimit(introText, MAX_TEXT_LENGTH);
  const [isSending, setIsSending] = React.useState(false);
  const [isAddingOwner, setIsAddingOwner] = React.useState(false);

  // Prefilled once, from the note the broker already wrote about each
  // candidate. Seeded rather than read live, because the broker edits these
  // for this send only and nothing is written back.
  const [commentByApplicationId, setCommentByApplicationId] = React.useState(
    () => getPrefilledComments(applications),
  );

  // Which boxes the broker has touched. The refetch below must never
  // overwrite words they already typed, and "has a value" is not the test:
  // a box they deliberately emptied is also theirs.
  const editedComments = React.useRef<{ [applicationId: string]: boolean }>({});

  // What each box was prefilled with. Compared against on send, because a
  // box still holding the note is the note coming back, and appending that
  // would copy it underneath itself on every mail.
  const prefilledComments = React.useRef<{ [applicationId: string]: string }>(
    getPrefilledComments(applications),
  );

  /**
   * The notes as they are now, not as the list last loaded them.
   *
   * A broker who annotates every candidacy and then opens this window was
   * seeing the notes from before, because the prefill came from the list in
   * the store and that is only refreshed by a page reload (Jonas,
   * 2026-10-06). A failed read here is not silent in the harmful sense: the
   * boxes keep the notes we already had, which is what they showed before.
   *
   * The newest note, asked for by sort rather than taken on trust from the
   * denormalized field on the candidacy: a candidacy can now carry several
   * notes, and which one that field holds is the API's business, not ours.
   * Where there is no note at all the legacy text on the main contact is
   * still what the broker was shown, so that is what the box falls back to.
   */
  React.useEffect(() => {
    let cancelled = false;

    const loadNotes = async () => {
      const fresh = await Promise.all(
        applications.map(application =>
          readLatestNote(application.id)
            .then(note => ({
              id: `${application.id}`,
              content: `${note?.content || getInternalNote(application)}`,
            }))
            .catch(() => undefined),
        ),
      );

      if (cancelled) {
        return;
      }

      setCommentByApplicationId(previous => {
        const next = { ...previous };

        fresh.forEach(loaded => {
          if (!loaded) {
            return;
          }

          prefilledComments.current[loaded.id] = loaded.content;

          if (!editedComments.current[loaded.id]) {
            next[loaded.id] = loaded.content;
          }
        });

        return next;
      });
    };

    loadNotes();

    return () => {
      cancelled = true;
    };
    // Once, when the window opens: a later refetch could land on top of
    // something the broker is in the middle of writing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The office's intro in each language, and the language of every owner.
   *
   * Both come from stores the dashboard already fills: the texts are
   * preferences like every other notification text, and an owner's language
   * sits on their contact, which getContactsByAccountIds fetches by account
   * id the way the contact list items do (DEV-3339).
   */
  const introByLocale = useSelector(
    (
      state: IRootStore,
    ): {
      [locale: string]: string | undefined;
    } => ({
      [ELocale.NL]: systemSelectors.getPreference<string>(
        state,
        CONFIG.PREFERENCE_OWNER_SHARE_INTRO_NL,
      ),
      [ELocale.FR]: systemSelectors.getPreference<string>(
        state,
        CONFIG.PREFERENCE_OWNER_SHARE_INTRO_FR,
      ),
      [ELocale.EN]: systemSelectors.getPreference<string>(
        state,
        CONFIG.PREFERENCE_OWNER_SHARE_INTRO_EN,
      ),
    }),
  );

  const localeByAccountId = useSelector((state: IRootStore) => {
    const result: { [accountId: string]: ELocale | undefined } = {};

    recipients.forEach(recipient => {
      result[`${recipient.accountId}`] = contactSelectors.getContact(
        state,
        undefined,
        recipient.accountId,
      )?.locale;
    });

    return result;
  });

  // Fetch the contacts the owners belong to, for their language. Cached, so
  // a second open of this window asks for nothing.
  React.useEffect(() => {
    const accountIds = recipients.map(recipient => recipient.accountId);

    if (accountIds.length === 0) {
      return;
    }

    dispatch(
      contactActions.getContactsByAccountIds.actions.start({ accountIds }),
    );
  }, [dispatch, recipients]);

  // The owner's language decides the intro, until the broker types.
  React.useEffect(() => {
    if (editedIntro.current) {
      return;
    }

    const locale = resolveOwnerLocale(localeByAccountId, selectedAccountIds);

    // The default Settings shows when the office never saved its own intro,
    // so the box opens with the text the office sees there instead of empty
    // (Jonas, 2026-10-08).
    setIntroText(`${introByLocale[locale] || getDefaultIntro(locale)}`);
    // Only the owner picked and the texts behind them: adding introText here
    // would reset the box on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountIds, localeByAccountId, introByLocale]);

  const loadRecipients = React.useCallback(async () => {
    try {
      setIsLoadingRecipients(true);
      const loaded = await getOwnerRecipients(property.id);
      setRecipients(loaded);

      // One owner who can be mailed is not a choice, so it is made for them.
      const mailable = loaded.filter(recipient => recipient.hasEmail);
      if (mailable.length === 1) {
        setSelectedAccountIds([mailable[0].accountId]);
      }
    } catch (error) {
      // Said out loud rather than swallowed: an empty list after a failed
      // load looks exactly like a property with no owners, and the broker
      // would go add one that already exists.
      setRecipients([]);
      toast({
        heading: getLocalizedText("general.error"),
        variation: "error",
      });
    } finally {
      setIsLoadingRecipients(false);
    }
  }, [property.id]);

  React.useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  const propertyOwners = (property.members || []).filter(
    (member: TPropertyMember) =>
      member.roles?.includes(EPropertyMemberTypes.Owner),
  );

  const mailableRecipients = recipients.filter(recipient => recipient.hasEmail);
  // The language the owner reads the mail in, and so the one the preview
  // below is written in.
  const ownerLocale = resolveOwnerLocale(localeByAccountId, selectedAccountIds);
  // Memoized: the intro and comment fields re-render on every keystroke.
  const includeAssessmentInPreview = includesAssessment(level);
  const preview = React.useMemo(
    () =>
      buildOwnerPreview({
        applications,
        locale: ownerLocale,
        includeAssessment: includeAssessmentInPreview,
      }),
    [applications, ownerLocale, includeAssessmentInPreview],
  );

  const canSend = !isIntroTooLong && !!selectedAccountIds.length && !isSending;

  const toggleRecipient = (accountId: number) =>
    setSelectedAccountIds(previous =>
      previous.includes(accountId)
        ? previous.filter(id => id !== accountId)
        : [...previous, accountId],
    );

  const handleSend = async () => {
    try {
      setIsSending(true);

      const result = await sendApplicationsToOwner(
        buildSendToOwnerPayload({
          propertyId: property.id,
          applications,
          recipientAccountIds: selectedAccountIds,
          level,
          includeProfileLink,
          ccSender,
          introText,
          commentByApplicationId,
        }),
      );

      // After the send and never before it: this records what an owner was
      // actually told, so a refused send must leave no trace. Once per
      // candidate however many owners received it, and only where the
      // broker wrote something of their own.
      if (includesAssessment(level)) {
        await Promise.all(
          applications.map(application => {
            const id = `${application.id}`;
            const comment = `${commentByApplicationId[id] || ""}`;

            if (
              !shouldAppendComment(comment, prefilledComments.current[id] || "")
            ) {
              return undefined;
            }

            // A failed write must not report the mail as failed: it went
            // out, and saying otherwise would invite a second send.
            return createShareNote(id, comment, new Date()).catch(
              () => undefined,
            );
          }),
        );
      }

      const { key, params } = getSuccessMessage(result, includeProfileLink);
      toast({ heading: getLocalizedText(key, params), variation: "success" });
      onSent();
      onClose();
    } catch (error) {
      // The server names what is wrong, such as a candidate belonging to
      // another property. Nothing is sent in part, so the fallback says so.
      toast({
        heading:
          getErrorMessage(getServerMessage(error)) ||
          getLocalizedText("application.send_to_owner.error"),
        variation: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal
      width="medium"
      onClose={onClose}
      heading={getLocalizedText("application.send_to_owner.title")}
      actions={[
        {
          content: getLocalizedText("system.cancel"),
          onClick: onClose,
          appearance: "outline",
        },
        {
          content: getLocalizedText("application.send_to_owner.submit"),
          onClick: handleSend,
          // Named rather than left to the default, which renders grey and
          // read as "you cannot send" while an owner was selected (Jonas,
          // 2026-10-06). It is grey only when it truly cannot send: no
          // owner picked, or a send already under way.
          appearance: "primary",
          isDisabled: !canSend,
          isSubmitting: isSending,
        },
      ]}
    >
      <Box mb={ESpacings.loose}>
        <DisplayText size="medium">
          {getLocalizedText("application.send_to_owner.recipients.label")}
        </DisplayText>

        {isLoadingRecipients ? (
          <Loading size={ELoadingSize.Small} />
        ) : (
          <>
            {recipients.map(recipient => (
              <Box
                key={recipient.accountId}
                mt={ESpacings.tight}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                }}
              >
                <span>
                  {recipient.name}
                  {!recipient.hasEmail && (
                    <>
                      {" "}
                      <TextStyle variation="subdued">
                        {getLocalizedText(
                          "application.send_to_owner.recipients.no_email",
                        )}
                      </TextStyle>
                    </>
                  )}
                </span>

                {/* An owner without an email is shown and not selectable:
                    leaving them out would read as "this owner was never
                    added" (DEV-3326). */}
                <Switch
                  isChecked={selectedAccountIds.includes(recipient.accountId)}
                  isDisabled={!recipient.hasEmail}
                  onChange={() => toggleRecipient(recipient.accountId)}
                />
              </Box>
            ))}

            {!mailableRecipients.length && (
              <Box mt={ESpacings.tight}>
                <TextStyle variation="subdued">
                  {getLocalizedText(
                    "application.send_to_owner.recipients.empty",
                  )}
                </TextStyle>
              </Box>
            )}

            <Box mt={ESpacings.tight}>
              {isAddingOwner ? (
                // The owner card of the property page, so an owner added here
                // is an owner of the property like any other, and the list is
                // read again once it is.
                <PropertyOwnersCard
                  property={property}
                  owners={propertyOwners}
                  onUpdate={() => {
                    setIsAddingOwner(false);
                    loadRecipients();
                  }}
                />
              ) : (
                <Button
                  appearance="link"
                  onClick={() => setIsAddingOwner(true)}
                >
                  {getLocalizedText("application.send_to_owner.recipients.add")}
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>

      <Box mb={ESpacings.loose}>
        <DisplayText size="medium">
          {getLocalizedText("application.send_to_owner.level.label")}
        </DisplayText>

        {/* Checkboxes rather than a row of tabs: these are three
            independent yes/no questions, and one of them is not a choice at
            all (Jonas, 2026-10-06). The design system's own Checkbox, the
            one the candidate list uses, so this window looks like the rest
            of the screen. */}
        <Box mt={ESpacings.tight} style={CHECKBOX_ROW}>
          <div style={CHECKBOX_CELL}>
            <Checkbox checked={true} error={false} disabled={true} />
          </div>
          <div>
            <div>
              {getLocalizedText("application.send_to_owner.share.basic")}
            </div>
            <TextStyle variation="subdued" size="small">
              {getLocalizedText("application.send_to_owner.level.basic.info")}
            </TextStyle>
          </div>
        </Box>

        <Box mt={ESpacings.tight} style={CHECKBOX_ROW}>
          <div style={CHECKBOX_CELL}>
            <Checkbox
              checked={includeAssessment}
              error={false}
              onChangeValue={() => setIncludeAssessment(previous => !previous)}
            />
          </div>
          <div>
            {getLocalizedText("application.send_to_owner.share.assessment")}
          </div>
        </Box>

        {/* Beside the two, not one of them: how much of the broker's own
            judgement is shared, and whether the owner may open the file,
            are two questions. */}
        <Box mt={ESpacings.base} style={{ ...CHECKBOX_ROW, gap: "12px" }}>
          <div style={CHECKBOX_CELL}>
            <Switch
              isChecked={includeProfileLink}
              onChange={() => setIncludeProfileLink(previous => !previous)}
            />
          </div>
          <div>
            <div>
              {getLocalizedText("application.send_to_owner.profile_link.label")}
            </div>
            <TextStyle variation="subdued" size="small">
              {getLocalizedText("application.send_to_owner.profile_link.info")}
            </TextStyle>
          </div>
        </Box>
      </Box>

      <Box mb={ESpacings.loose}>
        {/* A section title like the three above it, not the text field's own
            label: as a label it rendered small and grey, so one of the four
            sections of this window read as a form field and the other three
            as sections (Jonas, 2026-10-06). */}
        <DisplayText size="medium">
          {getLocalizedText("application.send_to_owner.intro.label")}
        </DisplayText>

        {/* The same editor the office writes its own intro with, so what a
            broker sees here is what they set in Settings (DEV-3339). The
            length shown is the text an owner reads: counting the markup
            would stop them at a number they cannot see. No label of its own:
            the section title above already says what it is, and a second
            copy of it read as a duplicate (Jonas, 2026-10-09). */}
        <RichTextInput
          value={introText}
          error={
            isIntroTooLong
              ? getLocalizedText("application.send_to_owner.intro.too_long", {
                  count: `${introLength}`,
                  max: `${MAX_TEXT_LENGTH}`,
                })
              : undefined
          }
          onChange={(value: string) => {
            editedIntro.current = true;
            setIntroText(value);
          }}
        />

        {/* Under the intro rather than with the sharing choices: it is about
            where the mail goes, not about what is in it (Jonas,
            2026-10-06). */}
        <Box mt={ESpacings.tight} style={CHECKBOX_ROW}>
          <div style={CHECKBOX_CELL}>
            <Checkbox
              checked={ccSender}
              error={false}
              onChangeValue={() => setCcSender(previous => !previous)}
            />
          </div>
          <div>{getLocalizedText("application.send_to_owner.cc_sender")}</div>
        </Box>
      </Box>

      {/* The mail as the owner will read it, one candidate at a time, and
          following the choices above: the broker sees exactly what goes out
          rather than a description of it (Jonas, 2026-10-08). Built from the
          keys and rules the mail itself uses, in the owner's language. */}
      <Box>
        <DisplayText size="medium">
          {getLocalizedText("application.send_to_owner.preview.heading")}
        </DisplayText>

        {preview.map(candidate => {
          const { id, rating } = candidate;

          return (
            <Box key={id} mt={ESpacings.base}>
              <TextStyle variation="strong">{candidate.heading}</TextStyle>
              <div>{candidate.name}</div>
              {candidate.lines.map(line => (
                <div key={line}>{`${DATA_LINE_DASH} ${line}`}</div>
              ))}

              {includeAssessmentInPreview && (
                <>
                  {/* Only stars the broker gave: the mail leaves the line
                      out for a candidate without any, and for 0, which is
                      how Rating clears them. Read-only, because the server
                      reads them off the application. */}
                  {!!rating && (
                    <div style={INLINE_ROW}>
                      {`${DATA_LINE_DASH} ${getLocalizedText(
                        OWNER_PREVIEW_KEY.rating,
                        {},
                        0,
                        ownerLocale,
                      )}:`}
                      <Rating value={rating} disabled={true} />
                    </div>
                  )}

                  <div>
                    {`${DATA_LINE_DASH} ${getLocalizedText(
                      OWNER_PREVIEW_KEY.comment,
                      {},
                      0,
                      ownerLocale,
                    )}:`}
                  </div>
                  <TextField
                    id={`send-to-owner-comment-${id}`}
                    name={`send-to-owner-comment-${id}`}
                    elementType="textarea"
                    maxLength={MAX_TEXT_LENGTH}
                    value={commentByApplicationId[id] || ""}
                    onChange={event => {
                      editedComments.current[id] = true;
                      setCommentByApplicationId(previous => ({
                        ...previous,
                        [id]: event.target.value,
                      }));
                    }}
                  />

                  <Button
                    appearance="link"
                    onClick={() => {
                      editedComments.current[id] = true;
                      setCommentByApplicationId(previous => ({
                        ...previous,
                        [id]: "",
                      }));
                    }}
                  >
                    {getLocalizedText(
                      "application.send_to_owner.comment.clear",
                    )}
                  </Button>

                  {candidate.noShow && (
                    <div>{`${DATA_LINE_DASH} ${candidate.noShow}`}</div>
                  )}
                </>
              )}

              {includeProfileLink && (
                <div style={LINK_PREVIEW}>
                  {getLocalizedText(
                    OWNER_PREVIEW_KEY.fileLink,
                    {},
                    0,
                    ownerLocale,
                  )}
                </div>
              )}
            </Box>
          );
        })}
      </Box>
    </Modal>
  );
};
